import { Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { ApiError } from "../../lib/http";
import {
  auditBadge, buildTree, duration, neioOff, statusTone, tokens,
  type Citation, type NeioStatus, type TraceDetail, type TraceNode, type TreeNode,
} from "../../lib/neio";
import { displayName } from "../../lib/words";
import { documentOpenPath, locatorPage } from "../../pages/documentViewFlow";
import { FeatureOff } from "../FeatureOff";
import { assetPath } from "../register/exports";
import { Empty, ErrorState, Loading } from "../States";

/** The standard refusal for a package this person is not scoped to. */
export function OutsideScope({ error }: { error: ApiError }) {
  return (
    <div className="state-box" role="status">
      <Lock aria-hidden="true" />
      <div><div className="state-title">Outside your packages</div><p className="muted">{error.message}</p></div>
    </div>
  );
}

/** Loading, switched off, refused or failed, or null when the content can be shown. A plain function: it holds no hooks. */
export function neioState({ what, loading, status, error, notFound, onRetry }: {
  what: string;
  loading: boolean;
  status: NeioStatus | undefined;
  error: unknown;
  notFound: React.ReactNode;
  onRetry?: () => void;
}): React.ReactElement | null {
  const off = neioOff(status, error);
  if (off) return <FeatureOff feature="NeIO Core" reason={off} />;
  if (loading) return <Loading what={what} />;
  if (error instanceof ApiError && error.status === 403) return <OutsideScope error={error} />;
  if (error instanceof ApiError && error.status === 404) return <>{notFound}</>;
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  return null;
}

function Json({ label, value }: { label: string; value: Record<string, unknown> | null | undefined }) {
  if (!value || Object.keys(value).length === 0) return null;
  return (
    <details className="trace-json">
      <summary>{label}</summary>
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

function Citations({ items, pkg }: { items: Citation[]; pkg: string | null }) {
  if (items.length === 0) return null;
  return (
    <div className="trace-cites">
      <div className="muted" style={{ fontSize: 12 }}>Cited ({items.length})</div>
      <ul>
        {items.map((c, i) => (
          <li key={i}>
            {/* The id is the register's content key; the open address looks the document view up from it. */}
            {(() => {
              const title = displayName(c.document_title) || c.document_id || "Unnamed document";
              const to = documentOpenPath({ key: c.document_id, pkg, page: locatorPage(c.locator) });
              return to
                ? <Link className="trace-cite-title" to={to} title="Open in the document view">{title}</Link>
                : <span className="trace-cite-title">{title}</span>;
            })()}
            {c.locator && <span className="muted">, {c.locator}</span>}
            {typeof c.score === "number" && <span className="muted num"> (score {c.score.toFixed(2)})</span>}
            {c.passage && <div className="mono-sm trace-passage">{c.passage}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function NodeRow({ node, pkg }: { node: TraceNode; pkg: string | null }) {
  const figures = [duration(node.duration_ms), tokens(node.total_tokens)].filter(Boolean).join(", ");
  return (
    <div className="trace-node">
      <div className="row" style={{ flexWrap: "wrap", gap: 8 }}>
        <span className="pill info trace-kind">{node.kind}</span>
        <strong>{node.name}</strong>
        <span className={`pill ${statusTone(node.status)}`}>{node.status}</span>
        {figures && <span className="muted num" style={{ fontSize: 12 }}>{figures}</span>}
      </div>
      {node.rationale && <p className="trace-rationale">{node.rationale}</p>}
      {node.error && <p className="trace-error" role="note">{node.error}</p>}
      <Json label="Inputs" value={node.inputs} />
      <Json label={node.kind === "llm" ? "Outputs (response is an excerpt)" : "Outputs"} value={node.outputs} />
      <Citations items={node.citations ?? []} pkg={pkg} />
    </div>
  );
}

function Branch({ items, pkg }: { items: TreeNode[]; pkg: string | null }) {
  return (
    <ul className="trace-tree">
      {items.map(({ node, children }) => (
        <li key={node.node_id}>
          <NodeRow node={node} pkg={pkg} />
          {children.length > 0 && <Branch items={children} pkg={pkg} />}
        </li>
      ))}
    </ul>
  );
}

/** One trace: who asked what, how long it took, and every step with its evidence. */
export function TraceBody({ detail }: { detail: TraceDetail }) {
  const { trace, audit } = detail;
  const meta = trace.meta ?? {};
  const badge = auditBadge(audit);
  const tree = buildTree(trace.nodes ?? []);
  const outcome = typeof meta.outcome === "string" ? meta.outcome : null;
  return (
    <div className="stack">
      <div className="stack" style={{ gap: 6 }}>
        <h2 style={{ fontSize: 24 }}>{trace.question || trace.request_id}</h2>
        <p className="row muted" style={{ flexWrap: "wrap", fontSize: 14 }}>
          {trace.actor && <span>By {trace.actor}</span>}
          {duration(trace.duration_ms) && <span className="num">{duration(trace.duration_ms)}</span>}
          {tokens(trace.total_tokens) && <span className="num">{tokens(trace.total_tokens)}</span>}
          <span className="num">{trace.node_count ?? (trace.nodes ?? []).length} steps</span>
          {outcome && <span>Outcome <span className={`pill ${outcome === "failed" ? "fail" : "info"}`}>{outcome.replace(/_/g, " ")}</span></span>}
          <span className={`pill ${badge.tone}`}>{badge.text}</span>
        </p>
        <p className="row muted" style={{ flexWrap: "wrap", fontSize: 13 }}>
          {meta.package && <span>Package <span className="mono">{meta.package}</span></span>}
          {meta.package && meta.tag && <Link to={assetPath(meta.package, meta.tag)}>Register entry for {meta.tag}</Link>}
          {meta.run_id && <Link to={`/modules/runs/${encodeURIComponent(meta.run_id)}`}>Module run</Link>}
          {meta.job_id && <Link to={`/extraction/${encodeURIComponent(meta.job_id)}`}>Extraction</Link>}
          <span className="mono-sm">{trace.request_id}</span>
        </p>
      </div>
      {tree.length === 0
        ? <Empty title="This trace recorded no steps" />
        : <section className="card" aria-label="Steps"><Branch items={tree} pkg={meta.package ?? null} /></section>}
    </div>
  );
}
